// a library to wrap and simplify api calls
import apisauce from "apisauce";
import storage from "redux-persist/lib/storage";
import qs from 'qs'
import { getDomain } from "../services/utils";
import { isDynamicContent } from "../services/contents";

// our "constructor"
const create = (baseURL = process.env.REACT_APP_API_ENDPOINT) => {
  // ------
  // STEP 1
  // ------
  //
  // Create and configure an apisauce-based api object.
  //
  let config =  {
    baseURL,
    headers: {
      "Cache-Control": "no-cache"
    },
    timeout: 60000,
    paramsSerializer: (params) => {
      return qs.stringify(params, {arrayFormat: 'indices'})
    }
  }

  const api = apisauce.create(config);

  const apiFiles = apisauce.create({
    ...config,
    responseType: 'blob'
  })

  const unauthorizedMonitor = async response => {
    if (((
      response.status === 400 && response.data.error == "token_expired")
      || response.status === 401
      || (response.status === 403 && response.data.message == "Password changed")
      || (response.status === 403 && response.data.message == "User deactivated")
    ) && await storage.getItem("authHeaders")) {
      const currentUrl = window.location.pathname;
      const isLoginPage = currentUrl.includes("/login");
      // Unless we are in the login from CMS url, reload page
      if (!isLoginPage) {
        await storage.setItem("authHeaders", null);
        let redirectUrl = "/reload/" + btoa(window.location.pathname);
        if (response.data.message == "Password changed" || response.data.message == "User deactivated") {
          redirectUrl +=  "/" + btoa(JSON.stringify(response.data));
        }
        window.location.href = redirectUrl;
      }
    }
  }
  api.addMonitor(unauthorizedMonitor);

  const authRequestTransform = request => async () => {
    const storageHeaders = await storage.getItem("authHeaders");

    if (!storageHeaders) {
      api.setHeader("access-token", null);
      apiFiles.setHeader("access-token", null);
      return;
    }

    const headers = JSON.parse(storageHeaders);

    api.setHeaders(headers);
    apiFiles.setHeaders(headers);

    request.headers = { ...request.headers, ...headers };
  };

  api.addAsyncRequestTransform(authRequestTransform);
  apiFiles.addAsyncRequestTransform(authRequestTransform);

  const whiteLabelNames = {
    latinad: 'latinad',
    grupoVia: 'grupo_via',
    mediaShake: 'mediashake',
    smartFit: 'smartfit',
    duoPrint:'duoprint',
    territorioDigital: 'territorio_digital',
    movImagen: 'movimagen',
    yoda: 'yoda',
    hakMedia: 'hak_media',
    blaster: 'blaster',
    laMediatica: 'la_mediatica',
    dv360: 'dv360',
  };

  const whiteLabelRequestTransform = request => async () => {
    const domain = getDomain(window.location.hostname);
    const whiteLabel = whiteLabelNames[domain];
     if (request.data instanceof FormData) {
      request.data.append('white_label_name', whiteLabel);
    } else {
      request.data = { ...request.data, white_label_name: whiteLabel };
    }
    request.url = `${request.url}${request.url.includes('?')?'&':'?'}white_label_name=${whiteLabel}`;
  }

  api.addAsyncRequestTransform(whiteLabelRequestTransform);
  apiFiles.addAsyncRequestTransform(whiteLabelRequestTransform);

  // ------
  // STEP 2
  // ------
  //
  // Define some functions that call the api.  The goal is to provide
  // a thin wrapper of the api layer providing nicer feeling functions
  // rather than "get", "post" and friends.
  //
  // I generally don't like wrapping the output at this level because
  // sometimes specific actions need to be take on `403` or `401`, etc.
  //
  // Since we can't hide from that, we embrace it by getting out of the
  // way at this level.
  //
  const setHeaders = headers => api.setHeaders(headers);

  const userLogin = (user, password, code2FA) =>
    api.post("/login", { login: user, password, two_factor_code: code2FA, remember: true });
  const getUserInfo = () => api.get("/users/me");
  const userRegister = user => api.post("/users", user);
  const companyRegister = company => api.post("/companies/request-account", company);
  const userResetPassword = (email, password, password_confirmation, token) =>
    api.post("/reset-password", {
      email,
      password,
      password_confirmation,
      token
    });
  const userForgotPassword = email => api.post("/forgot-password", { email });
  const resendVerificationEmail = email => api.get(`/email-verification/resend?email=${email}`);
  const userEmailUpdate = (id, first_name, last_name, username, email) =>
    api.put(`/users/${id}`, { first_name, last_name, username, email });
  // Same contract as the CMS profile: multipart with the image in the `avatar`
  // field, answers { avatar: <new url> }.
  const uploadUserAvatar = (id, file) => {
    const form = new FormData();
    form.append("avatar", file);
    return api.post(`/users/${id}/avatars`, form, { headers: { "content-type": "multipart/form-data;" } });
  };
  const userPasswordUpdate = (
    id,
    old_password,
    password,
    password_confirmation
  ) =>
    api.put(`/users/${id}/password`, {
      old_password,
      password,
      password_confirmation
    });

  const getDisplays = params => api.get("/displays/results", params);
  const postDisplays = params => api.post("/displays/results", params);
  const checkDisplayIds = params => api.post("/displays/check-ids", params);
  const getDisplaysMap = params => api.get("/displays/map", params);
  const getDisplayInfo = (id, clientId, currency, invoice_issuing_country, external_dsp_id, is_guaranteed) => {
    const queryParams = `external_dsp_id=${external_dsp_id}&&currency=${currency}&&invoice_issuing_country=${invoice_issuing_country}&&is_guaranteed=${is_guaranteed ? 1 : 0}`;
    const url = clientId ? `/displays/${id}/results?client_id=${clientId}&&${queryParams}` : `/displays/${id}/results?${queryParams}`;
    return api.get(url);
};
  const getExcelDisplays = params => apiFiles.post("/displays/reports/selected", params);
  const getCurrencyExchangeRate = () => api.get(`/currency-rate`);
  const getConversionRates = () => api.get(`/conversion-rates`);
  const getCategories = () => api.get(`/contents/content-categories`);

  const createCampaign = params => api.post(`/campaigns`, params);
  const retryCampaignAsyncOperation = (campaignId, operationId) =>
    api.post(`/campaigns/${campaignId}/async-operations/${operationId}/retry`);
  const editCampaign = (campaignId, params) => api.put(`/campaigns/${campaignId}`, params);
  const patchCampaignContentsDisplays = (campaignId, params) =>
    api.patch(`/campaigns/${campaignId}/contents-displays`, params);
  const getCampaigns = params => api.get(`users/me/campaigns`, params);
  const getCampaign = id => api.get(`/campaigns/${id}?show_graphics_evidences=1`);
  const getCampaignPriceBreakdown = campaignId => api.get(`/campaigns/${campaignId}/price-breakdown`);
  const deleteCampaign = campaignId => api.delete(`campaigns/${campaignId}`);
  const createReplicateCampaing = (campaignId, params) => api.post(`/campaigns/${campaignId}/replicate`, params);
  const requestSpecialCpm = (display_id, requested_cpm, currency) => api.post(`/displays/cpm-price-arrangement-request`, {display_id, requested_cpm, currency});
  const requestSpecialPrice = (display_id, requested_base_price_per_day, currency) => api.post(`/displays/price-arrangement-request`, {display_id, requested_base_price_per_day, currency});
  const createPlanner = (params) => api.post(`/campaigns/planners`, params);
  const optimizeBrief = (params) => api.post(`/campaigns/planners/optimize-brief`, params);
  const optimizeRules = (params) => api.post(`/campaigns/optimize-rules`, params);
  const justifyPlanner = (params) => api.post(`/campaigns/planners/justify`, params);
  const getCampaignCategories = () => api.get(`/campaigns/planners/campaign-categories`);
  const getCampaignPOI = () => api.get(`/campaigns/planners/poi-types`);
  const getCampaignScreens = () => api.get(`/campaigns/planners/screen-subtypes`);

  const getClients = (userId, params) => api.get(`users/${userId}/clients`, params);
  const getBrands = clientId => api.get(`clients/${clientId}/brands`);
  const getCountries = () => api.get(`countries`);
  const getLocation = () => api.get(`users-country`);
  const getExternalDsps = () => api.get(`external-dsps`);

  const getProposal = (reportData) =>
    api.post(`/proposal/shared`, { ...reportData });
  const getQuotes = (userId, params) => api.get(`users/${userId}/quotes`, params);
  const setArchivedQuote = (quoteId, archived) => api.put(`quotes/${quoteId}`, { archived });
  const getQuote = token => api.get(`quotes?token=${token}`);
  const createQuote = (quoteData) =>
    api.post("/quotes", {
      ...quoteData
    });
  const editQuote = (quoteId, quoteData) =>
    api.put(`/quotes/${quoteId}`, {
      ...quoteData
    });
  const deleteQuote = quoteId => api.delete(`quotes/${quoteId}`);

  const createCoupon = (campaignId, params) => api.post(`/campaigns/${campaignId}/coupons`, params);
  const deleteCoupon = (campaignId, couponId) => api.delete(`/campaigns/${campaignId}/coupons/${couponId}`);

  const getContent = id => api.get(`/contents/${id}`);
  const updateContent = (id, params) => api.put(`/contents/${id}`, params);
  const archiveContents = (ids) => api.put(`/contents/archive`, { ids });

  // Multipart upload of a new content (ad). Mirrors the antd action upload in
  // services/contents.js uploadContent: field `file`, name, origin 'dsp' and
  // tags[] (folder). Auth/white-label headers are applied by the request transforms.
  // `file` is a File for regular contents, or the URL string for dynamic ones
  // (type 'url' / 'url_reload'), which also require `preview` (an image).
  const createContent = ({ file, name, tags = [], type, preview, urlWhitelist = [], categoryId }) => {
    const form = new FormData();
    form.append("file", file);
    form.append("name", name);
    form.append("origin", "dsp");
    if (type) form.append("type", type);
    // Dynamic contents are always the custom-URL widget. Derived from `type` here
    // rather than passed in, so no caller can forget it or send it on a regular ad.
    if (isDynamicContent(type)) form.append("widget_type", "custom_url");
    if (preview) form.append("preview", preview);
    if (categoryId != null) form.append("content_category_id", categoryId);
    urlWhitelist.filter(url => url).forEach(url => form.append("url_whitelist[]", url));
    tags.filter(tag => tag != null).forEach(tag => form.append("tags[]", tag));
    return api.post("/contents", form, { headers: { "content-type": "multipart/form-data;" } });
  };

  const getContents = (id, params) => api.get(`/companies/${id}/contents`, params);
  const getContentsByUsers = (id, params) => api.get(`/users/${id}/contents`, params);

  const getTagsContents = (id) => api.get(`/companies/${id}/contents-tags`, {"from": 'dsp'});
  const getTagsContentsByUser = (id) => api.get(`/users/${id}/contents-tags`, {"from": 'dsp'});
  const createContentTag = (body) => api.post(`/contents-tags`, body);
  const updateContentTag = (id, body) => api.put(`/contents-tags/${id}`, body);
  const archiveContentTags = (ids) => api.delete(`/contents-tags/bulk-delete`, { ids });

  const updateCampaignContentDisplay = (relationId, newRelation, campaignId) => api.put(`/campaigns/${campaignId}/content_display/${relationId}`, newRelation);

  const uploadFile = file => {
    const form = new FormData();
    form.append("name", 'Diseño en canva');
    form.append("type", file.type);
    form.append("canva_id", file.canva_id);
    form.append("file", file.file);
    form.append("tags[]", file.tags);
    const headers = {
      "content-type": "multipart/form-data;"
    };

    return api.post("/contents", form, { headers });
  };

  // const getMercadoPagoPaymentMethods = () =>
  //   api.get("/payments/mercadopago/payment-methods");
  const createPayment = (campaignId, paymentData) =>
    api.post(`/campaigns/${campaignId}/payments`, { ...paymentData });
  const createPurchaseOrders = (companiesId, campaignId) =>
    api.post(`/companies/${companiesId}/campaigns/${campaignId}/purchase_orders`);
  const getCampaignPayments = (campaignId) =>
    api.get(`/campaigns/${campaignId}/payments`);
  const getCampaignStats = (campaignId) =>
    api.get(`/campaigns/${campaignId}/stats`);
  const getPayment = (paymentId) =>
    api.get(`/payments/${paymentId}`);
  const deleteCampaignPayment = (paymentId) =>
    api.delete(`/payments/${paymentId}`);
  const getMpInitPoint = (campaignId, paymentData) =>
    api.post(`/campaigns/${campaignId}/mp-init-point`, { ...paymentData });
  const getReport = (reportData) =>
    api.post(`/reports/shared`, { ...reportData });
  const getSingleReport = params => api.get("/reports", params);
  const getLastBillingInfo = (userId) =>
    api.get(`/users/${userId}/last-billing-information`);

  const getAudience = (data) =>
    api.post(`/audience`, { ...data });
  const getAudienceDatsWhy = (params) =>
    api.get(`/dw-audience`, params);
  const getAudienceHoursOfDay = (params) =>
    api.get(`/display_audience/hours_of_day`, params);
  const getAudienceQualitative = (displayId) =>
    api.get(`/display-audience-attributes/${displayId}`);
  const getAudienceQualitativeBulk = (displayIds) =>
    api.post(`/display-audience-attributes/bulk`, { display_ids: displayIds });
  const getAudienceDaysOfWeek = (params) =>
    api.get(`/display_audience/days_of_week`, params);
  const getProposalAudience = (data) => api.post('/proposal_audience', data);

  const createCampaignComment = (campaign, comments) => 
    api.post(`/campaigns/${campaign}/comments`, { comments });
  const editCampaignComment = (campaign, comments) => 
    api.put(`/campaigns/${campaign}/comments`, { comments });

  const editCampaignDate = (campaignId, params) =>
    api.put(`/campaigns/${campaignId}`, {
      name: params.name,
      start_date: params.start_date,
      end_date: params.end_date,
      is_test: params.is_test,
      contents_displays: params.contents_displays,
    });
  
  const changeProgrammaticContent = (campaignId, params) => 
    api.post(`/campaigns/${campaignId}/programmatic/content`, params);

  const getPlaceholders = (listResolution) =>
    api.post(`/contents/placeholder`, { 
      sizes: listResolution
    });
  
  const saveErrors = (error) => api.post(`/frontend_errors_log`, {
    origin: 'DSP',
    error_description: JSON.stringify(error),
  });

  const calculateCampaignPrices = (params) => api.post('/campaigns/prices/calculate', params);
  
  const getMediaCompanies = (params) => api.get('/companies/list-media-owners', params);

  // ------
  // STEP 3
  // ------
  //
  // Return back a collection of functions that we would consider our
  // interface.  Most of the time it'll be just the list of all the
  // methods in step 2.
  //
  // Notice we're not returning back the `api` created in step 1?  That's
  // because it is scoped privately.  This is one way to create truly
  // private scoped goodies in JavaScript.
  //
  return {
    setHeaders,
    userLogin,
    getUserInfo,
    userRegister,
    userResetPassword,
    userForgotPassword,
    resendVerificationEmail,
    userEmailUpdate,
    uploadUserAvatar,
    userPasswordUpdate,

    companyRegister,

    getDisplays,
    postDisplays,
    checkDisplayIds,
    getDisplaysMap,
    getDisplayInfo,
    getExcelDisplays,
    getCurrencyExchangeRate,
    getConversionRates,
    getCategories,

    getContent,
    getContents,
    getContentsByUsers,
    getTagsContents,
    getTagsContentsByUser,
    createContentTag,
    updateContentTag,
    archiveContentTags,
    updateContent,
    archiveContents,
    createContent,

    updateCampaignContentDisplay,
    createCampaign,
    retryCampaignAsyncOperation,
    editCampaign,
    patchCampaignContentsDisplays,
    getCampaigns,
    getCampaign,
    getCampaignPriceBreakdown,
    deleteCampaign,
    uploadFile,
    createReplicateCampaing,
    requestSpecialCpm,
    requestSpecialPrice,
    createPlanner,
    optimizeBrief,
    optimizeRules,
    justifyPlanner,
    getCampaignCategories,
    getCampaignPOI,
    getCampaignScreens,

    getClients,
    getBrands,
    getCountries,
    getLocation,
    getExternalDsps,

    getQuotes,
    getQuote,
    deleteQuote,
    createQuote,
    editQuote,
    setArchivedQuote,

    createCoupon,
    deleteCoupon,

    // getMercadoPagoPaymentMethods,
    createPayment,
    createPurchaseOrders,
    getCampaignPayments,
    getCampaignStats,
    deleteCampaignPayment,
    getPayment,
    getMpInitPoint,

    getReport,
    getProposal,
    getSingleReport,

    getLastBillingInfo,

    getAudience,
    getAudienceDatsWhy,
    getAudienceHoursOfDay,
    getAudienceDaysOfWeek,
    getAudienceQualitative,
    getAudienceQualitativeBulk,
    getProposalAudience,

    createCampaignComment,
    editCampaignComment,
    editCampaignDate,

    changeProgrammaticContent,

    getPlaceholders,

    // Save errors
    saveErrors,

    calculateCampaignPrices,

    getMediaCompanies,
  };
};

// let's return back our create method as the default.
export default {
  create
};